/**
 * Tracks keys for paginated list deduplication. Provides an exclusion list for
 * the API to skip already-rendered rows on subsequent page requests.
 *
 * Not an Angular service by design: each list owns its own instance to prevent
 * one feature's keys from suppressing another's rows (unlike a root singleton).
 *
 * `MAX_EXCLUDE_KEYS` caps outgoing list (OpenSearch limit: 65536, using 60000).
 * Keys never evict; `has()` always works. Past cap, consumers relying only on
 * exclusion (check records) may see old rows; those checking `has()` (DCV) won't.
 * Traversal never stalls—cursor boundary carries it. Growth bounded by session
 * (`clear()` on new search).
 */
export class SeenKeyTracker {
  // Cap on the outgoing exclusion list, not on the tracked set.
  static readonly MAX_EXCLUDE_KEYS = 60000;

  private readonly keys = new Set<string>();

  has(key: string): boolean {
    return this.keys.has(key);
  }

  add(key: string): void {
    this.keys.add(key);
  }

  clear(): void {
    this.keys.clear();
  }

  get size(): number {
    return this.keys.size;
  }

  // Returns the most recent keys, up to `MAX_EXCLUDE_KEYS`, for the next page.
  toExcludeList(): string[] {
    const keys = Array.from(this.keys);
    return keys.length > SeenKeyTracker.MAX_EXCLUDE_KEYS
      ? keys.slice(-SeenKeyTracker.MAX_EXCLUDE_KEYS)
      : keys;
  }
}
